import React, { useState } from 'react';
import clsx from 'clsx';
import { toast } from 'react-toastify';
import { aaiApi } from '../../services/aaiApi';
import { agentsApi } from '../../services/agentsApi';
import { ingest, ingestFile } from '../../orbit/lib/intakeApi';
import type { OrbitAsk, OrbitRoomDepartment, OrbitRoomPerson, OrbitRoomPurpose } from '../../types/aai';
import { Action, Chip, Drawer, Field, Input, Kicker, Select } from '../../components/aa';
import { handedLine, originLine, plural, responsibleLine, shortName } from './roomCopy';

/*
 * The Room's side panels. Every one opens over the page, does one thing, and
 * tells the panel to reload when it changed something.
 */

const errorText = (err: unknown, fallback: string): string => {
  const detail = (err as { response?: { data?: { detail?: unknown } } })?.response?.data?.detail;
  if (typeof detail === 'string' && detail) return detail;
  const message = (detail as { message?: unknown } | undefined)?.message;
  if (typeof message === 'string' && message) return message;
  return fallback;
};

const Row: React.FC<{ label: string; children: React.ReactNode }> = ({ label, children }) => (
  <div>
    <div className="text-[11px] font-semibold uppercase tracking-[0.1em] text-ink-soft">{label}</div>
    <div className="mt-1 text-[13.5px] leading-[21.5px] text-ink">{children}</div>
  </div>
);

const OptionChip: React.FC<{ on: boolean; onClick: () => void; children: React.ReactNode }> = ({ on, onClick, children }) => {
  let look = 'border-line bg-white text-ink hover:border-ink';
  if (on) look = 'border-ink bg-ink text-white';
  return (
    <button type="button" onClick={onClick} className={`inline-flex h-9 items-center rounded-full border px-3.5 text-[13px] font-medium transition-colors ${look} whitespace-nowrap`}>
      {children}
    </button>
  );
};

/* ── answering ─────────────────────────────────────────────────────────── */

export const AnswerInput: React.FC<{
  ask: OrbitAsk;
  value: unknown;
  onChange: (value: unknown) => void;
}> = ({ ask, value, onChange }) => {
  if (ask.kind === 'choose') {
    return (
      <div className="flex flex-wrap gap-2">
        {ask.options.map((o) => (
          <OptionChip key={o.value} on={value === o.value} onClick={() => onChange(o.value)}>{o.label}</OptionChip>
        ))}
      </div>
    );
  }
  if (ask.kind === 'choose_many') {
    const picked = Array.isArray(value) ? (value as string[]) : [];
    const toggle = (v: string) => {
      if (picked.includes(v)) {
        onChange(picked.filter((x) => x !== v));
        return;
      }
      onChange([...picked, v]);
    };
    return (
      <div className="flex flex-wrap gap-2">
        {ask.options.map((o) => (
          <OptionChip key={o.value} on={picked.includes(o.value)} onClick={() => toggle(o.value)}>{o.label}</OptionChip>
        ))}
      </div>
    );
  }
  if (ask.kind === 'number') {
    return <Input type="number" value={typeof value === 'number' || typeof value === 'string' ? String(value) : ''} onChange={(e) => onChange(e.target.value)} placeholder="A number" />;
  }
  let placeholder = 'Plain words are enough. Orbit turns them into things to verify.';
  if (ask.kind === 'people') placeholder = 'A name or an email. Several, separated by commas.';
  return (
    <textarea
      rows={4}
      value={typeof value === 'string' ? value : ''}
      onChange={(e) => onChange(e.target.value)}
      placeholder={placeholder}
      className="w-full rounded-act border border-input bg-white px-3 py-2 text-[13px] text-ink outline-none focus:border-ink"
    />
  );
};

const isEmptyAnswer = (value: unknown): boolean => {
  if (value === null || value === undefined) return true;
  if (typeof value === 'string') return value.trim() === '';
  if (Array.isArray(value)) return value.length === 0;
  return false;
};

/* ── one open question ─────────────────────────────────────────────────── */

export type AskMode = 'detail' | 'answer' | 'delegate';

export const AskDrawer: React.FC<{
  ask: OrbitAsk;
  mode: AskMode;
  me: string;
  people: OrbitRoomPerson[];
  canAnswer: boolean;
  canDelegate: boolean;
  canEdit: boolean;
  isPartner: boolean;
  onClose: () => void;
  onChanged: () => void;
}> = ({ ask, mode: initial, me, people, canAnswer, canDelegate, canEdit, isPartner, onClose, onChanged }) => {
  const [mode, setMode] = useState<AskMode>(initial);
  const [value, setValue] = useState<unknown>(null);
  const [to, setTo] = useState<string>('');
  const [note, setNote] = useState('');
  const [busy, setBusy] = useState(false);

  const targets = people.filter((p) => p.email !== me.toLowerCase() && !ask.chain.some((c) => c.from === p.email));
  const suggested = targets.find((p) => ask.scope && p.department === ask.scope && p.role === 'DEPARTMENT_HEAD')
    || targets.find((p) => p.role === 'OWNER');

  const save = async () => {
    if (isEmptyAnswer(value)) {
      toast.info('Choose or write an answer first.');
      return;
    }
    setBusy(true);
    try {
      await aaiApi.answerOrbitAsk(ask.id, value);
      if (isPartner) toast.success('Saved. The owner sees it as to confirm, with you as the source.');
      else toast.success('Answered. It moved to Collected with you as the source.');
      onChanged();
      onClose();
    } catch (err) {
      toast.error(errorText(err, 'Could not save the answer.'));
    } finally {
      setBusy(false);
    }
  };

  const hand = async () => {
    if (!to) {
      toast.info('Pick a person first.');
      return;
    }
    setBusy(true);
    try {
      await aaiApi.delegateOrbitAsk(ask.id, { to_email: to, note });
      const target = people.find((p) => p.email === to);
      if (target && target.onboarded) toast.success(`Handed to ${shortName(to, people)}. They see it on their Home.`);
      else toast.success(`Handed to ${shortName(to, people)}. No email sent; Orbit asks it in their onboarding.`);
      onChanged();
      onClose();
    } catch (err) {
      toast.error(errorText(err, 'Could not hand this on.'));
    } finally {
      setBusy(false);
    }
  };

  const later = async () => {
    setBusy(true);
    try {
      await aaiApi.skipOrbitAsk(ask.id);
      if (ask.chain.length > 0) toast.success(`Returned to ${shortName(ask.chain[ask.chain.length - 1].from, people)}.`);
      else toast.success('Kept for later. It stays in New to Ask.');
      onChanged();
      onClose();
    } catch (err) {
      toast.error(errorText(err, 'Could not update the question.'));
    } finally {
      setBusy(false);
    }
  };

  const drop = async () => {
    setBusy(true);
    try {
      await aaiApi.dropOrbitAsk(ask.id);
      toast.success('Question removed.');
      onChanged();
      onClose();
    } catch (err) {
      toast.error(errorText(err, 'Could not remove the question.'));
    } finally {
      setBusy(false);
    }
  };

  let delegateWord = 'Hand to someone';
  if (isPartner) delegateWord = 'Ask the owner';
  let returnWord = 'Later';
  if (ask.chain.length > 0) returnWord = 'Return it';

  const footer = (() => {
    if (mode === 'answer') {
      return (
        <div className="flex flex-wrap justify-between gap-2">
          <Action variant="ghost" onClick={() => setMode('detail')}>Back</Action>
          <Action onClick={save} disabled={busy}>Save answer</Action>
        </div>
      );
    }
    if (mode === 'delegate') {
      return (
        <div className="flex flex-wrap justify-between gap-2">
          <Action variant="ghost" onClick={() => setMode('detail')}>Back</Action>
          <Action onClick={hand} disabled={busy}>{delegateWord}</Action>
        </div>
      );
    }
    return (
      <div className="flex flex-wrap items-center justify-between gap-2">
        <div className="flex flex-wrap gap-2">
          {canAnswer && <Action onClick={() => setMode('answer')}>Answer</Action>}
          {canDelegate && targets.length > 0 && <Action variant="quiet" onClick={() => setMode('delegate')}>{delegateWord}</Action>}
        </div>
        <div className="flex flex-wrap gap-2">
          <Action variant="ghost" onClick={later} disabled={busy}>{returnWord}</Action>
          {canEdit && <Action variant="ghost" onClick={drop} disabled={busy}>Remove</Action>}
        </div>
      </div>
    );
  })();

  const handed = handedLine(ask, people);
  let howToAnswer = 'Here, in that person\'s onboarding, or from their Home. Text, a choice or a number.';
  if (isPartner) howToAnswer = 'Your answer is recorded with you as the source and shown to the owner as to confirm.';

  return (
    <Drawer
      onClose={onClose}
      kicker={<Kicker>New to Ask</Kicker>}
      badge={<Chip tone="run" dot>{ask.status}</Chip>}
      title={ask.question}
      sub={ask.why || undefined}
      footer={footer}
    >
      <div className="space-y-5">
        {mode === 'detail' && (
          <>
            <Row label="Responsible now">{responsibleLine(ask, me, people)}{ask.responsible_email ? ` · ${ask.responsible_email}` : ''}</Row>
            <Row label="Origin">{originLine(ask, people)}</Row>
            {handed && <Row label="History">{handed}</Row>}
            {ask.chain.length > 0 && ask.chain[ask.chain.length - 1].note && (
              <Row label="Note">“{ask.chain[ask.chain.length - 1].note}”</Row>
            )}
            {ask.onboarding && <Row label="Asked in onboarding">Yes. It counts toward the person's Continue onboarding card.</Row>}
            <Row label="How it can be answered">{howToAnswer}</Row>
          </>
        )}
        {mode === 'answer' && (
          <>
            <Field label="Your answer"><AnswerInput ask={ask} value={value} onChange={setValue} /></Field>
            <p className="m-0 text-[12.5px] leading-[20px] text-ink-dim">{howToAnswer}</p>
          </>
        )}
        {mode === 'delegate' && (
          <>
            <Field label="Hand it to" hint="Orbit suggests the person whose role fits. Anyone below you is allowed.">
              <div className="flex flex-wrap gap-2">
                {targets.map((p) => {
                  let tag = p.role.replace(/_/g, ' ').toLowerCase();
                  if (suggested && suggested.email === p.email) tag = 'Orbit suggests';
                  return (
                    <OptionChip key={p.email} on={to === p.email} onClick={() => setTo(p.email)}>
                      {p.name}<span className="ml-2 text-[11px] opacity-70">{tag}</span>
                    </OptionChip>
                  );
                })}
              </div>
            </Field>
            <Field label="A line of context (optional)">
              <Input value={note} onChange={(e) => setNote(e.target.value)} placeholder="Why them, or what you already know" />
            </Field>
            <p className="m-0 text-[12.5px] leading-[20px] text-ink-dim">
              Someone who has not onboarded yet is not emailed; Orbit asks them inside their onboarding. Someone already onboarded sees it on their Home and is told once.
            </p>
          </>
        )}
      </div>
    </Drawer>
  );
};

/* ── add a question ───────────────────────────────────────────────────── */

export const AddQuestionDrawer: React.FC<{
  purpose: OrbitRoomPurpose;
  bucket: string;
  scope: string;
  me: string;
  people: OrbitRoomPerson[];
  isPartner: boolean;
  onClose: () => void;
  onCreated: () => void;
}> = ({ purpose, bucket, scope, me, people, isPartner, onClose, onCreated }) => {
  const [question, setQuestion] = useState('');
  const [why, setWhy] = useState('');
  const [who, setWho] = useState<string>('');
  const [onboarding, setOnboarding] = useState(false);
  const [busy, setBusy] = useState(false);

  const targets = people.filter((p) => p.email !== me.toLowerCase());
  let meLabel = 'Me';
  if (isPartner) meLabel = 'Me, on the client\'s behalf';

  const add = async () => {
    if (!question.trim()) {
      toast.info('Write the question first.');
      return;
    }
    setBusy(true);
    try {
      await aaiApi.createOrbitAsk({
        purpose, bucket, scope, question: question.trim(), why: why.trim(), kind: 'say',
        responsible_email: who || undefined, onboarding,
      });
      if (who) toast.success(`Added. Orbit asks ${shortName(who, people)} from their next conversation.`);
      else toast.success('Added to New to Ask.');
      onCreated();
      onClose();
    } catch (err) {
      toast.error(errorText(err, 'Could not add the question.'));
    } finally {
      setBusy(false);
    }
  };

  return (
    <Drawer
      onClose={onClose}
      kicker={<Kicker>New to Ask</Kicker>}
      title="Add a question"
      sub="Is there anything additional you want Orbit to ask? Orbit words it for the person and asks it in their next conversation."
      footer={<div className="flex justify-end gap-2"><Action variant="ghost" onClick={onClose}>Cancel</Action><Action onClick={add} disabled={busy}>Add</Action></div>}
    >
      <div className="space-y-5">
        <Field label="Question">
          <textarea rows={3} value={question} onChange={(e) => setQuestion(e.target.value)} placeholder="What should Orbit find out?" className="w-full rounded-act border border-input bg-white px-3 py-2 text-[13px] text-ink outline-none focus:border-ink" />
        </Field>
        <Field label="Why it matters" hint="One line, shown next to the question.">
          <Input value={why} onChange={(e) => setWhy(e.target.value)} placeholder="What an answer unlocks" />
        </Field>
        <Field label="Responsible" hint="Leave it to Orbit and it picks by role: the department head for a department, the owner for the company.">
          <div className="flex flex-wrap gap-2">
            <OptionChip on={who === ''} onClick={() => setWho('')}>Orbit picks</OptionChip>
            {!isPartner && <OptionChip on={who === me.toLowerCase()} onClick={() => setWho(me.toLowerCase())}>{meLabel}</OptionChip>}
            {targets.map((p) => (
              <OptionChip key={p.email} on={who === p.email} onClick={() => setWho(p.email)}>{p.name}</OptionChip>
            ))}
          </div>
        </Field>
        <label className="flex items-center gap-2.5 text-[13px] text-ink">
          <input type="checkbox" checked={onboarding} onChange={(e) => setOnboarding(e.target.checked)} />
          Ask it during onboarding, not just later
        </label>
      </div>
    </Drawer>
  );
};

/* ── add context ──────────────────────────────────────────────────────── */

export const AddContextDrawer: React.FC<{
  clientId: string;
  onClose: () => void;
  onDone: () => void;
}> = ({ clientId, onClose, onDone }) => {
  const [note, setNote] = useState('');
  const [busy, setBusy] = useState(false);
  const [upload, setUpload] = useState<{ name: string; pct: number; state: 'reading' | 'done' | 'failed'; facts: number } | null>(null);

  const saveNote = async () => {
    if (!note.trim()) {
      toast.info('Write something first, or drop a document.');
      return;
    }
    setBusy(true);
    try {
      const res = await ingest('paste', note.trim(), clientId);
      toast.success(`Read. ${plural(res.deltas?.length || 0, 'fact', 'facts')} updated.`);
      onDone();
      onClose();
    } catch (err) {
      toast.error(errorText(err, 'Could not read the note.'));
    } finally {
      setBusy(false);
    }
  };

  // Reading a document takes a few seconds server-side. The bar creeps to 90
  // while the request runs and lands on 100 with the count when it returns.
  const drop = async (file: File | null) => {
    if (!file) return;
    setBusy(true);
    setUpload({ name: file.name, pct: 6, state: 'reading', facts: 0 });
    const tick = window.setInterval(() => {
      setUpload((u) => {
        if (!u || u.state !== 'reading' || u.pct >= 90) return u;
        return { ...u, pct: u.pct + 7 };
      });
    }, 350);
    try {
      const res = await ingestFile('doc', file, file.name, clientId);
      const facts = (res as unknown as { deltas?: unknown[] }).deltas?.length || 0;
      setUpload({ name: file.name, pct: 100, state: 'done', facts });
      toast.success(`${file.name} read. ${plural(facts, 'fact', 'facts')} updated; departments and people it names wait to be confirmed.`);
      onDone();
      window.setTimeout(onClose, 1400);
    } catch (err) {
      setUpload({ name: file.name, pct: 100, state: 'failed', facts: 0 });
      toast.error(errorText(err, 'Could not read the document.'));
    } finally {
      window.clearInterval(tick);
      setBusy(false);
    }
  };

  let uploadLine = 'Reading';
  if (upload && upload.state === 'done') uploadLine = `Read. ${plural(upload.facts, 'fact', 'facts')} updated.`;
  if (upload && upload.state === 'failed') uploadLine = 'Could not read this file.';

  return (
    <Drawer
      onClose={onClose}
      kicker={<Kicker>Orbit Room</Kicker>}
      title="Add context"
      sub="Orbit reads whatever you add and updates Collected and New to Ask. Nothing is asked twice."
      footer={<div className="flex justify-end gap-2"><Action variant="ghost" onClick={onClose}>Cancel</Action><Action onClick={saveNote} disabled={busy}>Save note</Action></div>}
    >
      <div className="space-y-5">
        {!upload && (
          <label className="flex cursor-pointer flex-col items-center gap-1 rounded-act border border-dashed border-line-strong bg-canvas px-5 py-6 text-center text-[13px] text-ink-dim hover:border-ink">
            <span className="font-semibold text-ink">Drop a document</span>
            <span>SOP, org chart, tools export, strategy. PDF, DOC, CSV, PNG.</span>
            <input type="file" className="hidden" onChange={(e) => drop(e.target.files && e.target.files[0])} disabled={busy} />
          </label>
        )}
        {upload && (
          <div className="flex items-center gap-3 rounded-act border border-line bg-white px-4 py-3" role="status" aria-live="polite">
            <span className="grid h-9 w-8 shrink-0 place-items-center rounded border border-line bg-card font-mono text-[8px] font-bold text-ink">{(upload.name.split('.').pop() || 'DOC').toUpperCase().slice(0, 4)}</span>
            <div className="min-w-0 flex-1">
              <div className="truncate text-[13px] font-semibold text-ink">{upload.name}</div>
              <div className="mt-1.5 h-[3px] overflow-hidden rounded-full bg-line-soft"><span className={clsx('block h-full transition-[width] duration-300', upload.state === 'failed' ? 'bg-[#fd2751]' : 'bg-ink')} style={{ width: `${upload.pct}%` }} /></div>
              <div className="mt-1 text-[12px] text-ink-dim">{uploadLine}{upload.state === 'reading' && <span className="ml-1 inline-block animate-pulse">…</span>}</div>
            </div>
            {upload.state === 'failed' && <Action size="sm" variant="ghost" onClick={() => setUpload(null)}>Try again</Action>}
          </div>
        )}
        <Field label="Or write a note">
          <textarea rows={5} value={note} onChange={(e) => setNote(e.target.value)} placeholder="Anything Orbit should know about the company." className="w-full rounded-act border border-input bg-white px-3 py-2 text-[13px] text-ink outline-none focus:border-ink" />
        </Field>
      </div>
    </Drawer>
  );
};

/* ── departments and people ────────────────────────────────────────────── */

const NEW_HEAD = '__new__';

export const AddDepartmentDrawer: React.FC<{
  people: OrbitRoomPerson[];
  onClose: () => void;
  onDone: () => void;
}> = ({ people, onClose, onDone }) => {
  const [name, setName] = useState('');
  const [head, setHead] = useState('');
  const [newHeadEmail, setNewHeadEmail] = useState('');
  const [busy, setBusy] = useState(false);

  const add = async () => {
    if (!name.trim()) {
      toast.info('Give the department a name.');
      return;
    }
    let leadEmail = head;
    if (head === NEW_HEAD) {
      leadEmail = newHeadEmail.trim().toLowerCase();
      if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(leadEmail)) {
        toast.info('Give the head a work email, so Orbit knows them when they join.');
        return;
      }
    }
    setBusy(true);
    try {
      await aaiApi.createDepartment({ name: name.trim(), lead_email: leadEmail || undefined, allow_no_head: true });
      toast.success('Department added. Its fields appear as open questions.');
      onDone();
      onClose();
    } catch (err) {
      toast.error(errorText(err, 'Could not add the department.'));
    } finally {
      setBusy(false);
    }
  };

  return (
    <Drawer
      onClose={onClose}
      kicker={<Kicker>Departments and people</Kicker>}
      title="Add department"
      sub="Then fill its context in yourself, or hand it to the head. Orbit already knows headcount from the roster."
      footer={<div className="flex justify-end gap-2"><Action variant="ghost" onClick={onClose}>Cancel</Action><Action onClick={add} disabled={busy}>Add</Action></div>}
    >
      <div className="space-y-5">
        <Field label="Name"><Input value={name} onChange={(e) => setName(e.target.value)} placeholder="e.g. IT and Security" /></Field>
        <Field label="Head (optional)" hint="Adding a head does not invite them. Without one, who runs it becomes an open question on the department.">
          <Select value={head} onChange={(e) => setHead(e.target.value)}>
            <option value="">Add later</option>
            <option value={NEW_HEAD}>Someone not on the roster yet</option>
            {people.map((p) => <option key={p.email} value={p.email}>{p.name}</option>)}
          </Select>
        </Field>
        {head === NEW_HEAD && (
          <Field label="Head's work email" hint="They are put on the roster as this department's head, without an invite. When they join with this email, Orbit already knows who they are and hands them the department's questions.">
            <Input type="email" value={newHeadEmail} onChange={(e) => setNewHeadEmail(e.target.value)} placeholder="name@company.com" />
          </Field>
        )}
      </div>
    </Drawer>
  );
};

export const AddPersonDrawer: React.FC<{
  clientId: string;
  departments: OrbitRoomDepartment[];
  onClose: () => void;
  onDone: () => void;
}> = ({ clientId, departments, onClose, onDone }) => {
  const [name, setName] = useState('');
  const [email, setEmail] = useState('');
  const [role, setRole] = useState('AI_PARTICIPANT');
  const [dept, setDept] = useState('');
  const [busy, setBusy] = useState(false);

  const add = async () => {
    if (!email.trim()) {
      toast.info('A work email is needed.');
      return;
    }
    setBusy(true);
    try {
      await agentsApi.addWorkspaceMember(clientId, {
        email: email.trim(), name: name.trim() || undefined, role, department_id: dept || undefined, send_email: false,
      });
      toast.success('Added to the roster. No email sent.');
      onDone();
      onClose();
    } catch (err) {
      toast.error(errorText(err, 'Could not add the person.'));
    } finally {
      setBusy(false);
    }
  };

  return (
    <Drawer
      onClose={onClose}
      kicker={<Kicker>Departments and people</Kicker>}
      title="Add person"
      sub="No email is sent. The person is context for Orbit until the owner invites them, and takes no seat on the plan until that invite goes out."
      footer={<div className="flex justify-end gap-2"><Action variant="ghost" onClick={onClose}>Cancel</Action><Action onClick={add} disabled={busy}>Add</Action></div>}
    >
      <div className="space-y-5">
        <Field label="Name"><Input value={name} onChange={(e) => setName(e.target.value)} placeholder="Full name" /></Field>
        <Field label="Work email"><Input value={email} onChange={(e) => setEmail(e.target.value)} placeholder="name@company.com" /></Field>
        <Field label="Role">
          <Select value={role} onChange={(e) => setRole(e.target.value)}>
            <option value="AI_PARTICIPANT">Employee</option>
            <option value="DEPARTMENT_HEAD">Department Head</option>
            <option value="AI_CHAMPION">AI Champion</option>
          </Select>
        </Field>
        <Field label="Department">
          <Select value={dept} onChange={(e) => setDept(e.target.value)}>
            <option value="">Not placed yet</option>
            {departments.map((d) => <option key={d.id} value={d.id}>{d.name}</option>)}
          </Select>
        </Field>
      </div>
    </Drawer>
  );
};

export const ImportRosterDrawer: React.FC<{
  clientId: string;
  onClose: () => void;
  onDone: () => void;
}> = ({ clientId, onClose, onDone }) => {
  const [busy, setBusy] = useState(false);

  const upload = async (file: File | null) => {
    if (!file) return;
    setBusy(true);
    try {
      const csv = await file.text();
      const res = await agentsApi.uploadWorkspaceMembersCsv(clientId, csv, { send_email: false });
      toast.success(`${plural(res.data.rows, 'row', 'rows')} read. Zero emails sent.`);
      onDone();
      onClose();
    } catch (err) {
      toast.error(errorText(err, 'Could not read the file.'));
    } finally {
      setBusy(false);
    }
  };

  return (
    <Drawer
      onClose={onClose}
      kicker={<Kicker>Departments and people</Kicker>}
      title="Import a roster"
      sub="Name, email, department, title. Departments are created as needed. No emails are sent and no seats are used; invites are a separate, deliberate step, and each one takes a seat."
      footer={<div className="flex justify-end"><Action variant="ghost" onClick={onClose}>Close</Action></div>}
    >
      <label className="flex cursor-pointer flex-col items-center gap-1 rounded-act border border-dashed border-line-strong bg-canvas px-5 py-8 text-center text-[13px] text-ink-dim hover:border-ink">
        <span className="font-semibold text-ink">Drop a CSV</span>
        <span>Any columns. Orbit detects email, name, department and title.</span>
        <input type="file" accept=".csv,text/csv" className="hidden" onChange={(e) => upload(e.target.files && e.target.files[0])} disabled={busy} />
      </label>
    </Drawer>
  );
};
